import { useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'

import { useApi } from '@tabeeb/modules/shared/utils/API'
import { useSelector } from '@tabeeb/store'
import { TenantPermission } from '@tabeeb/enums'
import { hasTenantPermission } from '@tabeeb/modules/permissions/selectors'

import { CorporationsQueryKeys } from '../queries'

export interface UseCurrentCorporationHookProps {
  enabled?: boolean
  ignorePermissions?: boolean
}

const useCurrentCorporation = (
  { enabled = true, ignorePermissions = false }: UseCurrentCorporationHookProps = {
    enabled: true,
    ignorePermissions: false,
  }
) => {
  const { tenantServiceApi: api } = useApi()

  const hasManageCertificatesCorporationScopePermission = useSelector((state) =>
    hasTenantPermission(state, TenantPermission.ManageCorporationCertificatesAndQualifications)
  )

  const hasManageCorporationUsersPermission = useSelector((state) =>
    hasTenantPermission(state, TenantPermission.ManageCorporationUsers)
  )

  const hasSyncCorporationTenantSettingsPermission = useSelector((state) =>
    hasTenantPermission(state, TenantPermission.ManageCorporationTenantSettings)
  )

  const hasSyncCorporationBillingSettingsPermission = useSelector((state) =>
    hasTenantPermission(state, TenantPermission.ManageCorporationBillingSettings)
  )

  const query = useQuery({
    queryKey: [CorporationsQueryKeys.CurrentCorporation],
    queryFn: () => api.corporations.getCurrentAsync(),
    refetchOnMount: false,
    enabled:
      enabled &&
      (ignorePermissions ||
        hasManageCertificatesCorporationScopePermission ||
        hasManageCorporationUsersPermission ||
        hasSyncCorporationTenantSettingsPermission ||
        hasSyncCorporationBillingSettingsPermission),
  })

  return useMemo(
    () => ({
      loading: query.isLoading,
      corporation: query.data,
      canManageUsers: Boolean(query.data && hasManageCorporationUsersPermission),
      canManageCorporationCertificates: Boolean(query.data && hasManageCertificatesCorporationScopePermission),
      canManageCorporationQualifications: Boolean(query.data && hasManageCertificatesCorporationScopePermission),
      canSyncCorporationTenantSettings: Boolean(query.data && hasSyncCorporationTenantSettingsPermission),
      canSyncCorporationBillingSettings: Boolean(query.data && hasSyncCorporationBillingSettingsPermission),
      onReload: query.refetch,
    }),
    [
      hasManageCertificatesCorporationScopePermission,
      hasManageCorporationUsersPermission,
      hasSyncCorporationBillingSettingsPermission,
      hasSyncCorporationTenantSettingsPermission,
      query.data,
      query.isLoading,
      query.refetch,
    ]
  )
}

export default useCurrentCorporation
